import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Input, NumericTextBox, RadioGroup, Switch } from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { debounce } from "lodash";
import { setWorkflowCustomVariables } from "../../../../actions/taskSidebar";
import { button, icon, label, notifyIcon, number, quote } from "../../../../config";
import { useTeamChipPersistence } from "../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import { getNotification } from "../../../../utils/common";
import ShowIfSection from "../../../Forms/ShowIfSection";
import { useFormInteraction } from "../../../Forms/contexts/FormInteractionContext";
import { deleteWorkflowOrTeamChipVariable } from "../../../TeamSetting/TeamChips/projectVariables.helper";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { useVariablesContext } from "../context/variableContext";
import { usePayloadBuilder } from "../hooks/usePayloadBuilder";
import { useAdminVariableHelper, useFormChipHelper, useLockTeamChipPresetFields, useUniqueVariableNames } from "../hooks/workflowVariables.hooks";
import "../workflowVariable.scss";
import { CHIP_TYPE_DATA, WORKFLOW_VARIABLE_LABELS } from "../workflowVariables.constants";
import ExpressionEditor from "./ExpressionEditor";
import VariableNameHeader from "./common/VariableNameHeader";

/**
 * used to render workflow variable configuration for input type variables
 * @author {Prachi Jain}
 */
const InputConfiguration = ({
  isFormVariable,
  usedWorkflowVariableIds = [],
  onRemoveFromForm,
  formData,
  formId,
  onPanelClose,
  showClickToRemoveFromFormBanner = true,
  handleShowIfChange = () => {},
}) => {
  const [isNameEmpty, setIsNameEmpty] = useState(false);
  const [isEditName, setIsEditName] = useState(false);
  const [showExpressionEditor, setShowExpressionEditor] = useState(false);
  const { selectedElement } = useFormInteraction();
  const {
    variableName,
    setVariableName,
    isRequired,
    setIsRequired,
    isPlaceholder,
    setIsPlaceholder,
    placeholderText,
    setPlaceholderText,
    isDefaultAnswer,
    setIsDefaultAnswer,
    defaultValue,
    setDefaultValue,
    isMaxLength,
    setIsMaxLength,
    isMinLength,
    setIsMinLength,
    maxLength,
    setMaxLength,
    minLength,
    setMinLength,
    chipType,
    setChipType,
    isDuplicateName,
    hasVarChanges,
    setHasVarChanges,
    isAutomaticallySetValue,
    setIsAutomaticallySetValue,
    setExpressionValue,
    isMultiLine,
    setIsMultiLine,
    lineCount,
    setLineCount,
    showChipName,
    setShowChipName,
  } = useVariablesContext();
  const {
    customWorkflowVariables,
    setCustomWorkflowVariables,
    isNewWorkflowVariable,
    setIsNewWorkflowVariable,
    selectedVariable,
    setSelectedVariable,
    workflow,
    setToggleVariableList,
  } = useWorkflowContext();
  const { buildTextPayload } = usePayloadBuilder();
  const teamChipPersist = useTeamChipPersistence();
  const { saveVariableForExistingWorkflow, saveVariableForNewWorkflow } = useAdminVariableHelper();
  const { checkIsDuplicateName } = useUniqueVariableNames();
  const { updateFormChipProperty, isFormSpecificVisible } = useFormChipHelper();
  const [changeCounter, setChangeCounter] = useState(0);

  const dispatch = useDispatch();

  // Check if the chip is used in the form (for Form Specific properties)
  const isChipUsedInForm = useMemo(() => {
    if (!isFormVariable) return true; // Not relevant for workflow designer
    return usedWorkflowVariableIds.includes(selectedVariable?.id);
  }, [isFormVariable, usedWorkflowVariableIds, selectedVariable?.id]);

  const lockTeamChipPresetFields = useLockTeamChipPresetFields(isFormVariable, selectedVariable);

  // Find linked admin variable name
  const linkedVariableName = useMemo(() => {
    return isNewWorkflowVariable ? selectedVariable?.variableName : selectedVariable?.variableId?.name;
  }, [selectedVariable, isNewWorkflowVariable]);

  const saveInputVariable = async () => {
    if (isNameEmpty) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (isDuplicateName) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (isMaxLength && maxLength < 3) {
      getNotification(quote.VARIABLE_MAX_VALUE, notifyIcon.WARNING_ICON);
      return;
    }
    let payload = buildTextPayload();
    let savedVariable = null;

    if (workflow?.Id) {
      const currentId = payload?.id || selectedVariable?.id;
      if (typeof currentId === "number" && !isNewWorkflowVariable) {
        delete payload.id;
      }
      savedVariable = await saveVariableForExistingWorkflow(payload);
      if (savedVariable && setSelectedVariable) {
        setSelectedVariable(savedVariable);
      }
    } else {
      savedVariable = await saveVariableForNewWorkflow(payload);
      if (teamChipPersist?.projectId && !savedVariable) {
        return null;
      }
      if (!savedVariable) {
        savedVariable = { ...selectedVariable, attributes: { ...selectedVariable?.attributes, ...payload.attributes } };
      }
    }
    setHasVarChanges(false);
    return savedVariable;
  };

  /**
   * Debounced auto-save function using lodash debounce
   * Automatically saves input variable configuration after 2 seconds of inactivity
   * @author Abhishek Dhillon
   */
  const debouncedAutoSave = useCallback(
    debounce(async () => {
      try {
        await saveInputVariable();
      } catch (error) {
        console.error("Auto-save failed:", error);
      }
    }, 2000),
    [saveInputVariable]
  );

  /**
   * Common function to handle variable changes and trigger auto-save
   * Replaces all handleVariableChange() calls
   * @author Abhishek Dhillon
   */
  const handleVariableChange = useCallback(() => {
    setHasVarChanges(true);
    setChangeCounter((prev) => prev + 1);
  }, [setHasVarChanges]);

  useEffect(() => {
    if (isNewWorkflowVariable && changeCounter === 0) {
      handleVariableChange();
    }
  }, []);

  /**
   * Auto-save effect with debouncing for form variables only
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (isFormVariable && changeCounter > 0) {
      debouncedAutoSave();
    }
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [changeCounter, isFormVariable]);

  /** Team Chips uses projectVariables delete when no workflow Id */
  const removeVariableHandler = async () => {
    const removedId = selectedVariable?.id;
    const deleteSucceeded = await deleteWorkflowOrTeamChipVariable({
      selectedVariable,
      workflow,
      isNewWorkflowVariable,
      teamChipPersist,
    });
    if (!deleteSucceeded) return;
    setToggleVariableList(false);
    setIsNewWorkflowVariable(false);
    setSelectedVariable(null);
    const filteredVariableList = customWorkflowVariables?.filter((variable) => variable.id !== removedId);
    setCustomWorkflowVariables(filteredVariableList);
    dispatch(setWorkflowCustomVariables(filteredVariableList));
  };

  /**
   * Handles variable name change with validation and change tracking
   * @author Abhishek Dhillon
   * @param {Event} event - Input change event
   */
  const handleVariableNameChange = (event) => {
    const newVariableName = event.target.value;
    setVariableName(newVariableName);
    checkIsDuplicateName(newVariableName?.trim());
    if (!newVariableName?.trim()) {
      setIsNameEmpty(true);
      setHasVarChanges(false);
      return;
    }
    setIsNameEmpty(false);
    handleVariableChange();
  };

  /**
   * Handles max length change with validation and change tracking
   * @author Abhishek Dhillon
   * @param {Event} e - Input change event
   */
  const handleMaxLengthChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setMaxLength(value);
      handleVariableChange();
      return;
    }
    setMaxLength(value);
    handleVariableChange();
  };

  /**
   * Handles validation notification for max length
   * @author Abhishek Dhillon
   */
  const handleNotification = () => {
    if (maxLength < 3) {
      getNotification(quote.VARIABLE_MAX_VALUE, notifyIcon.WARNING_ICON);
      setHasVarChanges(false);
      return;
    }
    handleVariableChange();
  };

  const minProps = useMemo(() => {
    if (typeof minLength === "number") {
      return { min: minLength ? (minLength >= 0 ? minLength : 0) : 0 };
    }
  }, [minLength]);

  return (
    <div className='position-relative'>
      <div className='d-flex flex-column border-bottom input-configuration-section overflow-y-auto overflow-x-hidden dropdown-configuration-section'>
        <div className='justify-content-between'>
          <VariableNameHeader
            isNameEmpty={isNameEmpty}
            setIsNameEmpty={setIsNameEmpty}
            selectedVariable={selectedVariable}
            removeVariableHandler={removeVariableHandler}
            handleVariableChange={handleVariableChange}
            formData={formData}
            formId={formId}
            onPanelClose={onPanelClose}
            isDeleteDisabled={teamChipPersist?.allowDelete === false}
            isFormVariable={isFormVariable}
            onVariableNameCommitted={!isFormVariable ? saveInputVariable : undefined}
          />
        </div>
        {showClickToRemoveFromFormBanner && isChipUsedInForm && onRemoveFromForm && (
          <div className='form-group mb-2'>
            <div
              className='information-panel w-100 d-flex panel-info cursor-pointer'
              onClick={() => {
                if (onRemoveFromForm && selectedVariable?.id) {
                  onRemoveFromForm(selectedVariable.id);
                }
              }}>
              <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
              <div className='panel-body'>
                <div className='panel-message'>{quote.CLICK_TO_REMOVE_FROM_FORM}</div>
              </div>
            </div>
          </div>
        )}
        {/* Info banner when chip is not added to form */}
        {!isChipUsedInForm && (
          <div className='form-group mb-2 cursor-default'>
            <div className='form-group mb-2 cursor-default'>
              <div className='information-panel w-100 d-flex panel-info'>
                <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
                <div className='panel-body'>
                  <div className='panel-message'>{quote.UNUSED_CHIP_INFO}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className='custom-variable-properties pr-2'>
          {/* Automatically Set Value Toggle - Only show in form builder when formId exists */}
          {isFormVariable && isFormSpecificVisible && (
            <>
              <div className='form-section-label mb-2 mt-1 bold '>Form Specific</div>
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='automatically-set-value-conf' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.AUTOMATICALLY_SET_VALUE}{" "}
                    {isAutomaticallySetValue && (
                      <span
                        className='cursor-pointer'
                        style={{ color: "#1976d2", textDecoration: "underline" }}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowExpressionEditor(true);
                        }}>
                        ({WORKFLOW_VARIABLE_LABELS.EDIT_EXPRESSION})
                      </span>
                    )}
                  </label>
                  <Switch
                    id='automatically-set-value-conf'
                    className='mb-1 toggle-switch-margin'
                    checked={isAutomaticallySetValue}
                    disabled={!isChipUsedInForm}
                    onChange={() => {
                      const newValue = !isAutomaticallySetValue;
                      setIsAutomaticallySetValue(newValue);
                      updateFormChipProperty("autoSetValue", newValue);
                    }}
                  />
                </div>
              </div>
              {/* Show If - Placeholder for future implementation */}
              <ShowIfSection // Show If Section
                selectedElement={selectedElement}
                handleShowIfChange={handleShowIfChange}
              />

              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='default-answer-conf' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.DEFAULT_ANSWER}
                  </label>
                  <Switch
                    id='default-answer-conf'
                    className='mb-1 toggle-switch-margin'
                    checked={isDefaultAnswer}
                    disabled={isAutomaticallySetValue}
                    onChange={(e) => {
                      setIsDefaultAnswer(!isDefaultAnswer);
                      updateFormChipProperty("defaultValue", e.target.value ? defaultValue : null);
                    }}
                  />
                </div>
                {isDefaultAnswer && (
                  <Input
                    autoFocus
                    placeholder={WORKFLOW_VARIABLE_LABELS.ENTER_DEFAULT_ANSWER}
                    minLength={isMinLength ? minLength : null}
                    maxLength={isMaxLength ? maxLength : null}
                    value={defaultValue}
                    disabled={isAutomaticallySetValue}
                    onChange={(e) => {
                      setDefaultValue(e.target.value);
                      updateFormChipProperty("defaultValue", e.target.value || null);
                    }}
                  />
                )}
              </div>

              {
                <div className='form-group mb-1'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <label htmlFor='required-field' className='mr-3 font-weight-normal'>
                      {WORKFLOW_VARIABLE_LABELS.REQUIRED}
                    </label>
                    <Switch
                      id='required-field'
                      className='mb-1 toggle-switch-margin'
                      checked={isRequired}
                      disabled={isAutomaticallySetValue}
                      onChange={() => {
                        setIsRequired(!isRequired);
                        updateFormChipProperty("required", !isRequired);
                      }}
                    />
                  </div>
                  {/* {isRequired && (
                <Hint direction='end' className='d-flex align-items-center gap-1 custom-variable'>
                  {icon.WARNING}
                  <span>{WORKFLOW_VARIABLE_LABELS.FORM_REQUIRED_HINT}</span>
                </Hint>
              )} */}
                </div>
              }

              {/* Input Type Configuration */}
              <div className='form-group mb-1'>
                <div className='d-flex justify-content-between mb-2'>
                  <label
                    htmlFor='input-type-conf'
                    className='mr-2 mb-0 d-flex align-items-center font-weight-normal'
                    style={{ whiteSpace: "nowrap", height: "32px" }}>
                    Input Type
                  </label>
                  <div className='d-flex align-items-start gap-2'>
                    {/* Line Count Numeric Input with Label */}
                    <div className='d-flex flex-column align-items-center' style={{ width: "80px" }}>
                      <NumericTextBox
                        title='Line count'
                        id='line-count-conf'
                        value={lineCount}
                        min={isMultiLine ? 2 : 1}
                        max={10}
                        disabled={!isChipUsedInForm || !isMultiLine || isAutomaticallySetValue}
                        onChange={(e) => {
                          setLineCount(e.target.value);
                          updateFormChipProperty("lineCount", e.target.value);
                        }}
                        style={{
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                      <small className='text-muted mt-1' style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                        Line count
                      </small>
                    </div>

                    {/* Toggle Style Buttons */}
                    <div className='d-flex' style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid #d4d4d8" }}>
                      <button
                        type='button'
                        className={`btn ${!isMultiLine ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setIsMultiLine(false);
                          setLineCount(1);
                          updateFormChipProperty("isMultiLine", false);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: !isMultiLine ? "#007bff" : "#ffffff",
                          color: !isMultiLine ? "#ffffff" : "#6c757d",
                          borderRight: "1px solid #d4d4d8",
                        }}>
                        Single Line
                      </button>
                      <button
                        type='button'
                        className={`btn ${isMultiLine ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setIsMultiLine(true);
                          setLineCount(2);
                          updateFormChipProperty("isMultiLine", true);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: isMultiLine ? "#007bff" : "#ffffff",
                          color: isMultiLine ? "#ffffff" : "#6c757d",
                        }}>
                        Multi Line
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Chip Name Configuration - Form Specific */}
              <div className='form-group mb-1'>
                <div className='d-flex justify-content-between mb-2'>
                  <label
                    htmlFor='chip-name-conf'
                    className='mr-2 mb-0 d-flex align-items-center font-weight-normal'
                    style={{ whiteSpace: "nowrap", height: "32px" }}>
                    Chip Name
                  </label>
                  <div className='d-flex align-items-center gap-2'>
                    {/* Toggle Style Buttons */}
                    <div className='d-flex' style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid #d4d4d8" }}>
                      <button
                        type='button'
                        className={`btn ${showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setShowChipName(true);
                          updateFormChipProperty("showChipName", true);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: showChipName ? "#007bff" : "#ffffff",
                          color: showChipName ? "#ffffff" : "#6c757d",
                          borderRight: "1px solid #d4d4d8",
                        }}>
                        Show
                      </button>
                      <button
                        type='button'
                        className={`btn ${!showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setShowChipName(false);
                          updateFormChipProperty("showChipName", false);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: !showChipName ? "#007bff" : "#ffffff",
                          color: !showChipName ? "#ffffff" : "#6c757d",
                        }}>
                        Hide
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className='form-section-label mb-2 bold '>General </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='placeholder-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.PLACEHOLDER}
              </label>
              <Switch
                id='placeholder-conf'
                className='mb-1 toggle-switch-margin'
                checked={isPlaceholder}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsPlaceholder(!isPlaceholder);
                  handleVariableChange();
                }}
              />
            </div>
            {isPlaceholder && (
              <Input
                maxLength={150}
                autoFocus
                placeholder={WORKFLOW_VARIABLE_LABELS.ENTER_PLACEHOLDER}
                value={placeholderText}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setPlaceholderText(e.target.value);
                  handleVariableChange();
                }}
              />
            )}
          </div>

          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='max-length-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.MAX_TEXT_LENGTH}
              </label>
              <Switch
                id='max-length-conf'
                className='mb-1 toggle-switch-margin'
                checked={isMaxLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsMaxLength(!isMaxLength);
                  handleVariableChange();
                }}
              />
            </div>
            {isMaxLength && (
              <NumericTextBox
                rangeOnEnter={true}
                autoFocus
                placeholder={WORKFLOW_VARIABLE_LABELS.ENTER_MAX_LENGTH}
                value={maxLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={handleMaxLengthChange}
                onBlur={handleNotification}
                {...minProps}
              />
            )}
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='min-length-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.MIN_TEXT_LENGTH}
              </label>
              <Switch
                id='min-length-conf'
                className='mb-1 toggle-switch-margin'
                checked={isMinLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsMinLength(!isMinLength);
                  handleVariableChange();
                }}
              />
            </div>
            {isMinLength && (
              <NumericTextBox
                autoFocus
                rangeOnEnter={true}
                placeholder={WORKFLOW_VARIABLE_LABELS.ENTER_MIN_LENGTH}
                value={minLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setMinLength(e.target.value);
                  handleVariableChange();
                }}
                min={0}
              />
            )}
          </div>

          {/* Chip Name Configuration */}
        </div>
      </div>
      {showExpressionEditor && (
        <ExpressionEditor
          isOpen={showExpressionEditor}
          onClose={() => {
            setShowExpressionEditor(false);
          }}
          onSave={(expression) => {
            setExpressionValue(expression);
            setShowExpressionEditor(false);
          }}
          currentVariableName={variableName}
          expectedVariableType='text'
          selectedVariable={selectedVariable}
          workflow={workflow}
          isNewWorkflowVariable={isNewWorkflowVariable}
          onSaveVariable={saveInputVariable}
        />
      )}
      {!isFormVariable && (
        <div className='update-button-container w-100 pt-3 text-right'>
          <button
            className='btn btn-primary'
            disabled={isDuplicateName || isNameEmpty || !variableName || lockTeamChipPresetFields}
            onClick={saveInputVariable}>
            {isNewWorkflowVariable ? button.SAVE : label.UPDATE}
          </button>
        </div>
      )}
    </div>
  );
};

export default InputConfiguration;
